import React, { useState } from 'react';
import DashboardCard from '../../../shared/ui/cards/DashboardCard';
import config from '../../../config/config';
import './TestCard.css';

interface TestCardProps {
  siteId?: number;
}

interface PingResult {
  time: number;
  success: boolean;
  error?: string;
}

interface PingStats {
  min: number;
  max: number;
  avg: number;
  successCount: number;
  totalCount: number;
}

const TestCard: React.FC<TestCardProps> = ({ siteId }) => {
  const [isCollapsed, setIsCollapsed] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [pingResults, setPingResults] = useState<PingResult[]>([]);
  const [pingStats, setPingStats] = useState<PingStats | null>(null);
  const [apiUrl, setApiUrl] = useState<string>('');

  // Функция для выполнения одного пинга
  const performPing = async (): Promise<PingResult> => {
    const startTime = performance.now();
    const baseUrl = config.api.baseURL;
    
    try {
      // Создаем URL для пинга - используем базовый URL API
      // Убираем /api/v1 или /api/v2 из конца, чтобы получить базовый URL сервера
      let serverBaseUrl = baseUrl;
      if (baseUrl.endsWith('/api/v1') || baseUrl.endsWith('/api/v2')) {
        serverBaseUrl = baseUrl.substring(0, baseUrl.lastIndexOf('/api'));
      }
      
      // Используем простой endpoint - пробуем HEAD запрос к корню API
      // Если HEAD не поддерживается, используем GET к /api/v1/auth/status
      // Добавляем timestamp для избежания кеширования
      const url = `${serverBaseUrl}/api/v1/auth/status?t=${Date.now()}`;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 10000); // 10 секунд таймаут
      
      const response = await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        headers: {
          'Accept': 'application/json',
        },
        cache: 'no-cache',
      });
      
      clearTimeout(timeoutId);
      const endTime = performance.now();
      const pingTime = Math.round(endTime - startTime);
      
      // Любой ответ от сервера (даже 401) считается успешным пингом
      // Главное - сервер ответил, значит соединение работает
      if (response.status >= 200 && response.status < 500) {
        return { time: pingTime, success: true };
      } else {
        return { time: pingTime, success: false, error: `HTTP ${response.status}` };
      }
    } catch (error: any) {
      const endTime = performance.now();
      const pingTime = Math.round(endTime - startTime);
      
      if (error.name === 'AbortError') {
        return { time: pingTime, success: false, error: 'Таймаут (>10 сек)' };
      }
      
      return { time: pingTime, success: false, error: error.message || 'Ошибка соединения' };
    }
  };

  // Функция для запуска теста пинга
  const runPingTest = async () => {
    setIsTesting(true);
    setPingResults([]);
    setPingStats(null);
    setApiUrl(config.api.baseURL);
    
    const results: PingResult[] = [];
    const testCount = 5; // Количество попыток
    
    for (let i = 0; i < testCount; i++) {
      const result = await performPing();
      results.push(result);
      setPingResults([...results]);
      
      // Небольшая задержка между запросами
      if (i < testCount - 1) {
        await new Promise(resolve => setTimeout(resolve, 500));
      }
    }
    
    // Вычисляем статистику
    const successfulResults = results.filter(r => r.success);
    if (successfulResults.length > 0) {
      const times = successfulResults.map(r => r.time);
      const stats: PingStats = {
        min: Math.min(...times),
        max: Math.max(...times),
        avg: Math.round(times.reduce((a, b) => a + b, 0) / times.length),
        successCount: successfulResults.length,
        totalCount: testCount,
      };
      setPingStats(stats);
    } else {
      setPingStats({
        min: 0,
        max: 0,
        avg: 0,
        successCount: 0,
        totalCount: testCount,
      });
    }
    
    setIsTesting(false);
  };

  const getPingQuality = (ping: number): { label: string; color: string } => {
    if (ping < 50) return { label: 'Отлично', color: 'var(--color-success)' };
    if (ping < 100) return { label: 'Хорошо', color: 'var(--theme-primary)' };
    if (ping < 200) return { label: 'Удовлетворительно', color: 'var(--color-warning)' };
    return { label: 'Плохо', color: 'var(--color-danger)' };
  };

  // Определяем режим работы приложения
  const isDevelopment = process.env.NODE_ENV === 'development' || config.app.debug;
  const isLocalhost = typeof window !== 'undefined' && 
    (window.location.hostname === 'localhost' || 
     window.location.hostname === '127.0.0.1' ||
     window.location.hostname.includes('trycloudflare.com') ||
     window.location.hostname.includes('loca.lt') ||
     window.location.hostname.includes('ngrok.io'));

  return (
    <DashboardCard
      id="test-card"
      title="Тестовая карточка"
      icon="ri-flask-line"
      isCollapsible={true}
      isCollapsed={isCollapsed}
      onToggleCollapse={() => setIsCollapsed(!isCollapsed)}
      expandMode="inline"
    >
      {!siteId ? (
        <div className="test-card-placeholder">
          <i className="ri-flask-line"></i>
          <h3>Выберите проект</h3>
          <small>Для работы с тестовой карточкой необходимо выбрать конкретный проект</small>
        </div>
      ) : (
        <div className="test-card-content">
          <div className="ping-test-section">
            <div className="ping-test-header">
              <h3>
                <i className="ri-speed-line"></i>
                Тест скорости пинга
              </h3>
              <button
                className="ping-test-btn"
                onClick={runPingTest}
                disabled={isTesting}
              >
                {isTesting ? (
                  <>
                    <i className="ri-loader-4-line spinning"></i>
                    Тестирование...
                  </>
                ) : (
                  <>
                    <i className="ri-play-line"></i>
                    Запустить тест
                  </>
                )}
              </button>
            </div>

            {apiUrl && (
              <div className="ping-api-url">
                <i className="ri-link"></i>
                <span>{apiUrl}</span>
                {(isDevelopment || isLocalhost) && (
                  <span className="ping-mode-badge dev">
                    <i className="ri-bug-line"></i>
                    Dev режим
                  </span>
                )}
              </div>
            )}

            {(isDevelopment || isLocalhost) && (
              <div className="ping-warning">
                <i className="ri-information-line"></i>
                <div>
                  <strong>Внимание:</strong> Приложение работает в режиме разработки.
                  Результаты пинга могут быть хуже из-за:
                  <ul>
                    <li>Неоптимизированного кода (без минификации)</li>
                    <li>Дополнительного логирования и проверок</li>
                    <li>Dev tools браузера и hot reload</li>
                    <li>Медленного дебаг режима на сервере</li>
                  </ul>
                  В production режиме результаты будут лучше.
                </div>
              </div>
            )}

            {pingStats && (
              <div className="ping-stats">
                <div className="ping-stat-item">
                  <span className="ping-stat-label">Средний пинг:</span>
                  <span className="ping-stat-value" style={{ color: getPingQuality(pingStats.avg).color }}>
                    {pingStats.avg} мс
                  </span>
                  <span className="ping-stat-quality" style={{ color: getPingQuality(pingStats.avg).color }}>
                    ({getPingQuality(pingStats.avg).label})
                  </span>
                </div>
                <div className="ping-stat-item">
                  <span className="ping-stat-label">Минимальный:</span>
                  <span className="ping-stat-value">{pingStats.min} мс</span>
                </div>
                <div className="ping-stat-item">
                  <span className="ping-stat-label">Максимальный:</span>
                  <span className="ping-stat-value">{pingStats.max} мс</span>
                </div>
                <div className="ping-stat-item">
                  <span className="ping-stat-label">Успешных запросов:</span>
                  <span className="ping-stat-value">
                    {pingStats.successCount} / {pingStats.totalCount}
                  </span>
                </div>
              </div>
            )}

            {pingResults.length > 0 && (
              <div className="ping-results">
                <h4>Результаты тестов:</h4>
                <div className="ping-results-list">
                  {pingResults.map((result, index) => (
                    <div
                      key={index}
                      className={`ping-result-item ${result.success ? 'success' : 'error'}`}
                    >
                      <span className="ping-result-number">#{index + 1}</span>
                      <span className="ping-result-time">{result.time} мс</span>
                      {result.success ? (
                        <i className="ri-check-line ping-result-icon"></i>
                      ) : (
                        <>
                          <i className="ri-close-line ping-result-icon"></i>
                          {result.error && (
                            <span className="ping-result-error">{result.error}</span>
                          )}
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </DashboardCard>
  );
};

export default TestCard;
